import { Checkbox } from '@mantine/core'
import { Link } from 'react-router-dom'
import { IconStarFilled } from '@tabler/icons-react'
import type { EntradaItem } from '@/api/chamados'
import { PriorityBadge } from '@/components/chamados/PriorityBadge'
import { textoDaEspera, textoDaVez, textoDoMovimento } from './pastas'
import classes from './Entrada.module.css'

interface LinhaDoChamadoProps {
  item: EntradaItem
  viewerUserId: number | null | undefined
  /** Quem olha é membro de duas filas ou mais: a linha diz de qual fila é o chamado (spec
   *  2026-10-21 §5). */
  mostrarFila?: boolean
  /** A pasta tem a coluna Responsável (spec 2026-10-22 §2). */
  mostrarResponsavel?: boolean
  selecionado: boolean
  onSelecionar: (marcado: boolean) => void
  onAbrir: () => void
  /** O `state` do link do título, o mesmo que o clique na linha passa. */
  estadoAoAbrir?: unknown
}

/**
 * Uma linha por chamado, como o Mailbox (spec §3, §5): seleção · estrela · #id · solicitante ·
 * responsável (nas pastas que o têm) · prioridade (só Crítica e Alta) · ambiente · título — trecho · espera.
 *
 * Não usa o `ChamadoListItem` de maio: ele é um cartão por chamado (Paper com borda), a estrela
 * é emoji, a prioridade sai crua ("critical") e o protocolo em cinza `dimmed` — e a linha aqui é
 * uma só, densa, com espera e trecho que ele não tem.
 */
export function LinhaDoChamado({ item, viewerUserId, mostrarFila = false, mostrarResponsavel = false, selecionado, onSelecionar, onAbrir, estadoAoAbrir }: LinhaDoChamadoProps) {
  const souSolicitante = viewerUserId != null && item.requested_by_user_id === viewerUserId
  const solicitante = item.requester_name || item.requester_email || 'Solicitante desconhecido'
  const movimento = textoDoMovimento(item.ultimo_movimento, souSolicitante)
  const espera = textoDaEspera(item.espera_dias_uteis)
  const vez = textoDaVez(item.vez, souSolicitante)
  const etiquetaPrioridade = item.priority === 'critical' || item.priority === 'high'
  const fila = mostrarFila ? item.fila_nome : null
  const responsavel = mostrarResponsavel ? (item.assignee_name || item.assignee_email || null) : null

  return (
    <li
      className={classes.linha}
      data-testid="linha-do-chamado"
      data-negrito={item.negrito}
      // O clique em qualquer ponto da linha abre o chamado; teclado e leitor de tela usam o
      // link do título (uma linha com role de link não pode conter a caixa de seleção).
      onClick={onAbrir}
    >
      <span className={classes.selecao} onClick={(e) => e.stopPropagation()}>
        <Checkbox
          size="xs"
          checked={selecionado}
          onChange={(e) => onSelecionar(e.currentTarget.checked)}
          aria-label={`Selecionar o chamado #${item.id}`}
        />
      </span>
      <span className={classes.estrela}>
        {item.estrela && (
          // Derivada (§5.1): quem olha é o responsável. Não é favorito e não é clicável.
          <IconStarFilled size={14} role="img" aria-label="Você é o responsável" />
        )}
      </span>
      <span className={classes.id}>#{item.id}</span>
      <span className={classes.solicitante}>{solicitante}</span>
      {/* No computador o invólucro some (`display: contents`) e cada etiqueta ocupa a sua
          coluna; em tela estreita ele vira uma linha própria, com as duas lado a lado. */}
      <span className={classes.etiquetas}>
        {/* Responsável (spec 2026-10-22): coluna própria no computador; em tela estreita, sem
            cabeçalho, vai na faixa das etiquetas com o "com" na frente. */}
        {mostrarResponsavel && (
          <span className={classes.responsavel} data-testid="responsavel-da-linha">
            {responsavel && <><span className={classes.prefixoResponsavel}>com </span>{responsavel}</>}
          </span>
        )}
        <span className={classes.prioridade}>
          {etiquetaPrioridade && <PriorityBadge priority={item.priority} size="xs" />}
        </span>
        <span className={classes.ambiente}>
          {item.tenant_name && <span className={classes.etiquetaAmbiente}>{item.tenant_name}</span>}
        </span>
      </span>
      <Link
        to={`/chamados/${item.id}`}
        state={estadoAoAbrir}
        className={classes.conteudo}
        onClick={(e) => e.stopPropagation()}
        aria-label={`Chamado #${item.id}${fila ? `, fila ${fila}` : ''}: ${item.title}${item.negrito ? '. Movimento novo' : ''}. ${movimento}`}
      >
        {fila && <span className={classes.trecho} data-testid="fila-da-linha">{fila} · </span>}
        <span style={{ fontWeight: item.negrito ? 700 : 400 }}>{item.title}</span>
        <span className={classes.trecho}> — {movimento}</span>
      </Link>
      <span className={classes.espera}>
        <span className={classes.esperaNumero}>{espera}</span>
        <span className={classes.esperaVez}>{vez}</span>
      </span>
    </li>
  )
}
